import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Button,
  Card,
  CardMedia,
  CardContent,
  Link,
  useTheme,
  Divider,
  Paper,
  CircularProgress
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ProductCard from '../components/products/ProductCard';
import SeoHead from '../components/common/SeoHead';

// Firebase
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/firebase';

// 导入图片
import heroImage from '../assets/images/indexBg.png';
import ftab1 from '../assets/images/index/Ftab1.png';
import ftab2 from '../assets/images/index/Ftab2.png';
import ftab3 from '../assets/images/index/Ftab3.png';
import aiFirst from '../assets/images/index/aiFirst.png';
import aiSecond from '../assets/images/index/aiSecond.png';
import cuUnity from '../assets/images/index/cuUnity.png';
import cuR1 from '../assets/images/index/cuR1.png';
import cuR2 from '../assets/images/index/cuR2.jpg';
import cuR3 from '../assets/images/index/cuR3.png';
import cuR4 from '../assets/images/index/cuR4.jpg';
import cuR5 from '../assets/images/index/cuR5.jpg';
import cuB1 from '../assets/images/index/cuB1.png';
import btpng from '../assets/images/index/Btpng.png';

const Home = () => {
  const theme = useTheme();
  const [isEnglish, setIsEnglish] = useState(false);
  
  // 添加SEO结构化数据
  const homeSchemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "DestinyCrystal命理水晶 - AI算命、预约命理师与水晶手串销售",
    "description": "DestinyCrystal提供专业的AI算命、真人命理师预约及优质水晶手串销售，帮助您平衡能量、获得人生指引。",
    "url": "https://destiny-crystal.com/",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "AI算命服务",
          "url": "https://destiny-crystal.com/ai-fortune"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "命理师预约",
          "url": "https://destiny-crystal.com/fortune-teller"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "水晶手串销售",
          "url": "https://destiny-crystal.com/shop"
        }
      ]
    }
  };
  
  // AI文本内容
  const zh_ai = `爱情运势占卜
深入分析您的感情状况，预测爱情走向，助您把握缘分。
事业发展占卜
全面评估职业发展机遇，为您的事业决策提供指导
综合运势占卜
全方位解读您的运势，涵盖事业、感情、财运等多个方面。`;

  const en_ai = `Love Fortune Divination
Conduct an in-depth analysis of your relationship status, predict the direction of your love life, and help you seize the opportunity of fate.
Career Development Divination
Comprehensively evaluate the opportunities for career development and provide guidance for your career decisions.
Comprehensive Fortune Divination
Interpret your fortune in all aspects, covering multiple aspects such as career, relationships, and financial luck.`;

  const currentText = isEnglish ? en_ai : zh_ai;
  
  const toggleLanguage = () => {
    setIsEnglish(!isEnglish);
  };

  // 从Firestore获取产品数据
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        // 获取特色产品 - 根据状态和评分排序，限制3个结果
        const productsQuery = query(
          collection(db, 'products'),
          where('status', '==', 'active'),
          orderBy('price', 'desc'),
          limit(3)
        );
        
        const productsSnapshot = await getDocs(productsQuery);
        const productsList = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // 使用图片URL或默认图片
          image: doc.data().images && doc.data().images.length > 0 
            ? doc.data().images[0] 
            : btpng
        }));
        
        setFeaturedProducts(productsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching featured products:", error);
        setError("无法加载推荐产品");
        setLoading(false);
        
        // 如果出错，设置一些备用产品数据
        setFeaturedProducts([
          {
            id: 'backup1',
            name: '水晶手链',
            price: 299,
            image: btpng,
            description: '精美水晶手链，能量平衡，提升气场'
          },
          {
            id: 'backup2',
            name: '命理咨询服务',
            price: 358,
            image: btpng,
            description: '专业命理师一对一咨询服务'
          },
          {
            id: 'backup3',
            name: '紫水晶手串',
            price: 328,
            image: btpng,
            description: '高品质紫水晶手串，有助平静心灵'
          }
        ]);
      }
    };

    fetchFeaturedProducts();
  }, []);

  const goToShop = () => {
    // Navigate to shop page
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* SEO优化 */}
      <SeoHead
        title="DestinyCrystal命理水晶 - AI算命、预约命理师与水晶手串销售"
        description="DestinyCrystal提供专业的AI算命、真人命理师预约及优质水晶手串销售，帮助您平衡能量、获得人生指引。立即体验我们的命理服务和精选水晶产品！"
        canonicalUrl="https://destiny-crystal.com/"
        imageUrl="https://destiny-crystal.com/assets/images/indexBg.png"
        keywords={["算命", "水晶手串", "命理师", "紫水晶", "AI算命", "风水", "能量水晶"]}
        schemaData={homeSchemaData}
      />
      {/* Hero Section */}
      <Box className="hero" sx={{ position: 'relative' }}>
        <img 
          src={heroImage} 
          alt="Background" 
          style={{ width: '100%', display: 'block' }}
        />
        <Box 
          className="hero-content"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            width: '100%',
            padding: '0 2rem',
            color: 'white'
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            Know Yourself & Those Around You
          </Typography>
          <Box className="hero-buttons" sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button 
              component={RouterLink}
              to="/fortune-teller"
              variant="outlined"
              color="inherit"
              sx={{ borderRadius: '4px', px: 4, py: 1.5, borderColor: 'white' }}
            >
              Fortune Telling
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Popular Categories */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" align="center" sx={{ mb: 4 }}>
          热门类别
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <Box 
              component={RouterLink} 
              to="/shop"
              sx={{ 
                textDecoration: 'none', 
                color: 'inherit',
                display: 'block'
              }}
            >
              <Box 
                sx={{ 
                  overflow: 'hidden', 
                  borderRadius: 2,
                  mb: 2, 
                  '&:hover img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.5s ease'
                  }
                }}
              >
                <img 
                  src={ftab1} 
                  alt="Clear Quartz" 
                  style={{ 
                    width: '100%', 
                    display: 'block',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </Box>
              <Typography variant="h6">
                Clear Quartz
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Master healer and energy amplifier
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box 
              component={RouterLink} 
              to="/shop"
              sx={{ 
                textDecoration: 'none', 
                color: 'inherit',
                display: 'block'
              }}
            >
              <Box 
                sx={{ 
                  overflow: 'hidden', 
                  borderRadius: 2,
                  mb: 2, 
                  '&:hover img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.5s ease'
                  }
                }}
              >
                <img 
                  src={ftab2} 
                  alt="Crystal Category" 
                  style={{ 
                    width: '100%', 
                    display: 'block',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </Box>
              <Typography variant="h6">
                Clear Quartz
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Master healer and energy amplifier
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box 
              component={RouterLink} 
              to="/shop"
              sx={{ 
                textDecoration: 'none', 
                color: 'inherit',
                display: 'block'
              }}
            >
              <Box 
                sx={{ 
                  overflow: 'hidden', 
                  borderRadius: 2,
                  mb: 2, 
                  '&:hover img': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.5s ease'
                  }
                }}
              >
                <img 
                  src={ftab3} 
                  alt="Crystal Category" 
                  style={{ 
                    width: '100%', 
                    display: 'block',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </Box>
              <Typography variant="h6">
                Clear Quartz
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Master healer and energy amplifier
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      
      {/* AI Divination Section */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box 
              sx={{ 
                overflow: 'hidden', 
                borderRadius: 2,
                height: '100%',
                '&:hover img': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.5s ease'
                }
              }}
            >
              <img 
                src={aiFirst} 
                alt="AI Divination" 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.3s ease'
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box 
              sx={{ 
                overflow: 'hidden', 
                borderRadius: 2,
                height: '100%',
                '&:hover img': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.5s ease'
                }
              }}
            >
              <img 
                src={aiSecond} 
                alt="AI Divination" 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.3s ease'
                }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box 
              sx={{ 
                p: 3, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  AI 智能占卜（AI intelligent divination）
                </Typography>
                <Typography variant="body2" paragraph>
                  智能算法解读你的命运，推荐合适的水晶
                </Typography>
                <Typography variant="body2" paragraph>
                  The intelligent algorithm interprets your destiny and recommends suitable crystals.
                </Typography>
              </Box>
              
              <Box sx={{ maxHeight: '15em', display: 'flex', width: '100%', mb: 2 }}>
                <Box 
                  sx={{ 
                    overflowY: 'auto', 
                    flexGrow: 1, 
                    p: '1em 0.5em',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {currentText}
                </Box>
                <Box>
                  <label className="rocker rocker-small">
                    <input type="checkbox" checked={isEnglish} onChange={toggleLanguage} />
                    <span className="switch-left">中</span>
                    <span className="switch-right">英</span>
                  </label>
                </Box>
              </Box>
              
              <Button 
                className="anniu" 
                component={RouterLink}
                to="/ai-fortune"
                sx={{ 
                  mt: 2,
                  backgroundColor: '#9678b6',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#7d5ba6'
                  },
                  alignSelf: 'flex-start'
                }}
              >
                AI算命
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
      
      {/* Crystal Section */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', gap: '1.5em', mb: 2 }}>
                <Box 
                  sx={{ 
                    flex: 2,
                    overflow: 'hidden', 
                    borderRadius: 2,
                    height: '100%',
                    '&:hover img': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.5s ease'
                    }
                  }}
                >
                  <img 
                    src={cuUnity} 
                    alt="Crystal Unity" 
                    style={{ 
                      width: '100%', 
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                </Box>
                
                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between',
                  gap: '0.5em' 
                }}>
                  {/* 五张垂直排列的图片 */}
                  <Box 
                    sx={{ 
                      overflow: 'hidden', 
                      borderRadius: 2,
                      height: '19%',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.5s ease'
                      }
                    }}
                  >
                    <img 
                      src={cuR1} 
                      alt="Crystal" 
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                  </Box>
                  <Box 
                    sx={{ 
                      overflow: 'hidden', 
                      borderRadius: 2,
                      height: '19%',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.5s ease'
                      }
                    }}
                  >
                    <img 
                      src={cuR2} 
                      alt="Crystal" 
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                  </Box>
                  <Box 
                    sx={{ 
                      overflow: 'hidden', 
                      borderRadius: 2,
                      height: '19%',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.5s ease'
                      }
                    }}
                  >
                    <img 
                      src={cuR3} 
                      alt="Crystal" 
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                  </Box>
                  <Box 
                    sx={{ 
                      overflow: 'hidden', 
                      borderRadius: 2,
                      height: '19%',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.5s ease'
                      }
                    }}
                  >
                    <img 
                      src={cuR4} 
                      alt="Crystal" 
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                  </Box>
                  <Box 
                    sx={{ 
                      overflow: 'hidden', 
                      borderRadius: 2,
                      height: '19%',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.5s ease'
                      }
                    }}
                  >
                    <img 
                      src={cuR5} 
                      alt="Crystal" 
                      style={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.3s ease'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ textAlign: 'left', mt: 1.5, display: 'flex', flexDirection: 'column', height: '150px', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h5" sx={{ mb: 0, lineHeight: 1.2 }}>
                    寻找
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 0, lineHeight: 1.2 }}>
                    你的命理水晶
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 0, lineHeight: 1.2 }}>
                    Find Your Destiny Crystal
                  </Typography>
                </Box>
                <Button 
                  className="anniu" 
                  component={RouterLink}
                  to="/shop"
                  sx={{ 
                    backgroundColor: '#9678b6',
                    color: 'white',
                    alignSelf: 'flex-start',
                    '&:hover': {
                      backgroundColor: '#7d5ba6'
                    }
                  }}
                >
                  商城 SHOP
                </Button>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box 
              sx={{ 
                overflow: 'hidden', 
                borderRadius: 2,
                height: { md: '100%' },
                display: 'flex',
                '&:hover img': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.5s ease'
                }
              }}
            >
              <img 
                src={cuB1} 
                alt="Crystal" 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.3s ease'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      
      {/* Featured Products */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" align="center">
          不知道从哪里开始？
        </Typography>
        <Typography variant="h5" align="center" sx={{ mb: 2 }}>
          Not Sure Where To Start
        </Typography>
        <Typography variant="h6" align="center" sx={{ mb: 6, fontWeight: 'normal' }}>
          每一颗水晶都蕴含独特能量，为你的生活带来不同的祝福
        </Typography>
        
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress sx={{ color: '#9678b6' }} />
          </Box>
        ) : error ? (
          <Box textAlign="center" my={4}>
            <Typography color="error">{error}</Typography>
            <Button 
              component={RouterLink}
              to="/shop"
              variant="contained"
              sx={{ 
                mt: 2, 
                backgroundColor: '#9678b6',
                '&:hover': { backgroundColor: '#7d5ba6' }
              }}
            >
              浏览所有产品
            </Button>
          </Box>
        ) : (
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {featuredProducts.map(product => (
              <Grid item xs={12} sm={6} md={4} key={product.id}>
                <ProductCard 
                  product={{
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    image: product.images && product.images.length > 0 ? product.images[0] : product.image || btpng,
                    description: product.description || '暂无描述'
                  }} 
                />
              </Grid>
            ))}
          </Grid>
        )}
        
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6} sx={{ position: 'relative', left: { md: '2em' } }}>
            <img 
              src={btpng} 
              alt="About Us" 
              style={{ width: '100%', display: 'block' }}
            />
          </Grid>
          
          <Grid item xs={12} md={6} sx={{ position: 'relative', right: { md: '2em' } }}>
            <Paper
              elevation={3}
              sx={{ 
                p: 4, 
                borderRadius: 2,
                boxShadow: 'rgba(0, 0, 0, 0.15) 0px 15px 25px, rgba(0, 0, 0, 0.05) 0px 5px 10px' 
              }}
            >
              <Typography variant="h4" gutterBottom>
                Our Story
              </Typography>
              <Typography variant="body1" paragraph align="left">
                细腻设计，彰显内在：我们以低调的奢华，捕捉您的个人魅力。每一件作品都凝聚着我们对完美的追求，让晶石的纯净之美为您的气质加分。
              </Typography>
              <Typography variant="body1" paragraph align="left">
                灵性之光，温润如玉：水晶的微妙能量，悄然提升您的生活品质。每一颗晶石都蕴含着大自然的馈赠，为您带来内在的平和与力量。
              </Typography>
              <Button 
                className="anniu" 
                component={RouterLink}
                to="/about"
                sx={{ 
                  backgroundColor: '#9678b6',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#7d5ba6'
                  }
                }}
              >
                Learn More About Us
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;